/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useEffect, useRef, useState } from 'react'
import slugify from 'slugify'
import { useSearchParams } from 'react-router-dom'

import { Enum_Componentcategoriescategory_Gif_Placement } from 'api/graphql/types.generated'
import { renderPlacement } from './utils'
import { ProductType, TagType } from 'type/menu'
import SubCategory from '../subCategory'
import { FilterButton } from '../filterButton'
import { FilterModal } from '../filterModal'
import { filterProductByTags } from '../../../utils/filters'
import { ScrollToTopButton } from '../../scrollToTop'
import { normalizeString } from '../../../utils/string'
import { ProductV2 } from '../product-v2'
import SubCategoryV2 from '../subCategoryV2'

interface Props {
  title: string | undefined | null
  subtitle: string | undefined | null
  description: string | undefined | null
  footerDescription: string | undefined | null
  image: string | undefined | null
  imagePlacement: Enum_Componentcategoriescategory_Gif_Placement | null | undefined
  products: ProductType[]
  backgroundColor?: string | null
  hideProducts?: boolean | null | undefined
  hidden_products: number[]
  filterTitleSection1: string
  filterTitleSection2: string
  showFilters: boolean
  noMatchMessage?: string
  isArabic: boolean
}

export const CategoryV2: React.FC<Props> = ({
  title,
  subtitle,
  description,
  footerDescription,
  image,
  imagePlacement,
  products,
  backgroundColor,
  hideProducts,
  filterTitleSection1,
  filterTitleSection2,
  showFilters,
  hidden_products,
  noMatchMessage,
  isArabic
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const sectionRef = useRef<HTMLDivElement>(null)
  const [isCategoryOpen, setIsCategoryOpen] = useState<boolean>(false)
  const [currentSubCategoryTitle, setCurrentSubCategoryTitle] = useState<string | null>(null)

  const productsHaveSubCategoriesAccordion = products.some(
    (product) =>
      product.sub_category?.attributes?.title !== null && product.sub_category?.attributes?.is_accordion === true
  )
  const query = searchParams.get('categorie')

  const slug = slugify(title ?? '', { lower: true })
  const categoriesTitles: string[] = []
  const categories: string[] = []

  const subCategory = searchParams.get('sous_categorie')
  const decodedSubCategory = decodeURIComponent(subCategory || '')

  useEffect(() => {
    let requestRef: number

    const checkCoverage = () => {
      if (!sectionRef.current) return

      const rect = sectionRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight

      // Calcul de la zone visible de CETTE section précise
      const visibleTop = Math.max(0, rect.top)
      const visibleBottom = Math.min(viewportHeight, rect.bottom)
      const visibleHeight = Math.max(0, visibleBottom - visibleTop)

      const coverage = visibleHeight / viewportHeight

      // On récupère la valeur actuelle de l'URL au moment du calcul
      const currentParams = new URLSearchParams(window.location.search)
      if (currentParams.get('from') === 'click') {
        requestRef = requestAnimationFrame(checkCoverage)
        return
      }
      const currentCategorie = currentParams.get('categorie')

      // Condition : Si je couvre > 50% ET que je ne suis pas déjà l'actif
      if (coverage >= 0.5 && currentCategorie !== slug) {
        console.log(`Section ${slug} prend le contrôle (Coverage: ${Math.round(coverage * 100)}%)`)

        const newParams = new URLSearchParams(window.location.search)
        newParams.set('categorie', slug)

        setSearchParams(newParams, { replace: true })
      }

      requestRef = requestAnimationFrame(checkCoverage)
    }

    const v2CheckCoverage = () => {
      if (!sectionRef.current) return

      const rect = sectionRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight

      // 1. Calcul de la zone réellement visible (en pixels)
      const visibleTop = Math.max(0, rect.top)
      const visibleBottom = Math.min(viewportHeight, rect.bottom)
      const visibleHeight = Math.max(0, visibleBottom - visibleTop)

      // 2. Calcul du ratio par rapport au viewport (ton ancienne logique)
      const viewportCoverage = visibleHeight / viewportHeight

      // 3. Calcul du ratio par rapport à la taille de la section elle-même
      // Si ce ratio est de 1, cela signifie que TOUTE la div est affichée
      const sectionHeight = rect.height
      const sectionSelfVisibility = sectionHeight > 0 ? visibleHeight / sectionHeight : 0

      const currentParams = new URLSearchParams(window.location.search)
      if (currentParams.get('from') === 'click') {
        requestRef = requestAnimationFrame(checkCoverage)
        return
      }

      const currentCategorie = currentParams.get('categorie')

      // Condition : 50% du viewport OU 100% de la section elle-même
      const isHalfViewport = viewportCoverage >= 0.5
      const isFullyVisible = sectionSelfVisibility >= 0.99 // 0.99 pour éviter les soucis de pixels flottants

      if ((isHalfViewport || isFullyVisible) && currentCategorie !== slug) {
        console.log(
          `Section ${slug} active. Viewport: ${Math.round(viewportCoverage * 100)}%, Self: ${Math.round(sectionSelfVisibility * 100)}%`
        )

        const newParams = new URLSearchParams(window.location.search)
        newParams.set('categorie', slug)
        setSearchParams(newParams, { replace: true })
      }

      requestRef = requestAnimationFrame(checkCoverage)
    }

    requestRef = requestAnimationFrame(checkCoverage)

    return () => {
      cancelAnimationFrame(requestRef)
      console.log(`Section ${slug} libère le contrôle`)
    }
  }, [setSearchParams])

  useEffect(() => {
    const productFound = products.find(
      (product) => slugify(product.sub_category.attributes.title ?? '', { lower: true }) === decodedSubCategory
    )

    if (productFound) {
      setCurrentSubCategoryTitle(productFound.sub_category.attributes.title)
    }
  }, [decodedSubCategory, products])

  useEffect(() => {
    if (slug === query) {
      setIsCategoryOpen(true)
    } else {
      setIsCategoryOpen(false)
    }
  }, [query, slug])

  const setParams = () => {
    const queryService = searchParams.get('service')
    const querySubCategory = searchParams.get('sous_categorie')
    const queryHideReservation = searchParams.get('hide_reservation')
    let params = {}

    if (queryService) {
      params = { ...params, service: queryService }
    }

    if (querySubCategory) {
      params = { ...params, sous_categorie: querySubCategory }
    }

    if (queryHideReservation) {
      params = { ...params, hide_reservation: queryHideReservation }
    }

    if (searchParams.get('categorie') === slug) {
      setSearchParams(params, {})
    } else {
      setSearchParams({ ...params, categorie: slug })
    }
  }

  const [filteredTags, setFilteredTags] = useState<{ marketingTitles: string[]; titles: string[] }>({
    marketingTitles: [],
    titles: []
  })
  const [marketingTitles, setMarketingTitles] = useState<string[]>([])
  const [titles, setTitles] = useState<string[]>([])
  const [isFilterModalOpened, setIsFilterModalOpened] = useState(false)

  const thereAreTagsToFilter = marketingTitles.length > 0 || titles.length > 0
  const numberOfActivatedTags = filteredTags.marketingTitles.length + filteredTags.titles.length
  const { products: filteredProducts, isPerfectMatch } = filterProductByTags(products, filteredTags)
  const allFilteredTags = [...filteredTags.marketingTitles, ...filteredTags.titles]

  useEffect(() => {
    const tags = products.map((category) => category.product.attributes.tags).flat()

    const marketingTitles = tags.reduce((acc: string[], tag: TagType) => {
      if (acc.indexOf(tag.marketing_title) < 0) {
        acc.push(tag.marketing_title)
      }
      return acc
    }, [])
    const titles = tags.reduce((acc: string[], tag: TagType) => {
      if (acc.indexOf(tag.title) < 0) {
        acc.push(tag.title)
      }
      return acc
    }, [])

    setMarketingTitles(marketingTitles)
    setTitles(titles)
  }, [products, title])

  const showFilterModal = () => {
    setIsFilterModalOpened(true)
  }

  const handleOnFilter = ({ marketingTitles, titles }: { marketingTitles: string[]; titles: string[] }) => {
    setFilteredTags({
      marketingTitles,
      titles
    })

    const element = document.getElementById(`sub_category_${title}`)
    if (element) {
      const scroll = setTimeout(() => {
        element.scrollIntoView(true)
        clearTimeout(scroll)
      }, 10)
    }
  }

  if (hideProducts ?? false) {
    return <></>
  }

  return (
    <div
      ref={sectionRef}
      id={slug}
      //  style={{ scrollMarginTop: 95 }}
      key={`disclosure-${slug}-${isCategoryOpen}`}
      className="px-4 pt-6 pb-10 text-primary scroll-m-28"
    >
      {image && (
        <div className="relative top-[95px]">
          <img
            src={image}
            alt="gifs"
            className={`absolute z-30 w-[90px] aspect-square ${renderPlacement(imagePlacement)} zIndex`}
          />
        </div>
      )}

      <div className={description ? 'mb-10' : 'mb-5'}>
        <div className="flex items-center justify-center relative">
          <p
            translate="no"
            className="font-mammaTitle tracking-[0.02em] font-bold uppercase text-center max-w-[80%] text-[20px] z-10 relative bg-white px-3"
          >
            {title}
          </p>
          <hr className="border-0 border-t border-primary absolute top-1/2 left-0 w-full" />
        </div>
        {description && (
          <p className="max-w-[280px] whitespace-pre-wrap md:max-w-[448px] mt-3 text-center mx-auto text-[14px] leading-[120%]">
            {description}
          </p>
        )}
      </div>

      <div className="relative w-full">
        <div className="relative text-center text-primary">
          <div className="grid md:grid-cols-2 gap-y-5 gap-x-6">
            {filteredProducts?.map((product, idx: number) => {
              const isAccordion = Boolean(product.sub_category?.id)
              const isLittleAddition = Boolean(product.sub_category?.attributes?.is_little_addition)

              if (isAccordion) {
                const categoryTitle = product.sub_category?.attributes?.title
                const selected = products?.filter((p) => p?.sub_category?.attributes?.title === categoryTitle)

                if (categoryTitle && !categoriesTitles.includes(categoryTitle)) {
                  categoriesTitles.push(categoryTitle)

                  return (
                    <SubCategoryV2
                      key={product?.sub_category?.id}
                      isLittleAddition={isLittleAddition}
                      title={categoryTitle}
                      description={product.sub_category?.attributes?.description}
                      products={selected}
                      hiddenProductIds={hidden_products}
                      backgroundColor={backgroundColor}
                      isOpen={currentSubCategoryTitle === categoryTitle}
                      onClick={(subCategoryTitle) => {
                        setCurrentSubCategoryTitle(subCategoryTitle)
                      }}
                      noMatchMessage={noMatchMessage}
                      isArabic={isArabic}
                    />
                  )
                } else {
                  return null
                }
              }

              const title = product?.sub_category?.attributes?.title

              const showCategory = () => {
                if (categories.includes(title)) {
                  return false
                } else {
                  if (product?.sub_category?.attributes?.title === null) {
                    return false
                  } else {
                    categories.push(product?.sub_category?.attributes?.title!)
                    return true
                  }
                }
              }

              if (!hidden_products || !hidden_products.includes(+product.product.attributes.product_id)) {
                const productTitle = product.product.attributes.title?.trim()
                const productPrice = product.product.attributes.price?.trim()
                return (
                  <div
                    id="product"
                    key={product.product?.id}
                    className={`
                      ${!productTitle && !productPrice ? 'md:col-span-2' : ''} 
                      ${product?.product?.attributes?.best_of_product && product?.product?.attributes?.image ? 'md:col-span-2' : ''}
                    `}
                  >
                    <ProductV2
                      removeTopBorder={!!description && idx === 0 && !product.sub_category.attributes.title}
                      hidden_products={hidden_products}
                      product={product?.product}
                      subCategory={product.sub_category}
                      idx={idx}
                      showCategory={showCategory()}
                      filteredTags={allFilteredTags}
                      isPerfectMatchFilter={isPerfectMatch}
                      isArabic={isArabic}
                    />
                  </div>
                )
              } else {
                return null
              }
            })}
          </div>
          {/* Catégory Footer */}
          {footerDescription && (
            <div className="px-[30px]">
              <p className="whitespace-pre-wrap py-[20px] text-[12px] leading-[12px] text-center">
                {footerDescription}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
